// Replaces the old Tawk.to live-chat embed (ChatWidget.jsx) with a simple
// raise-a-ticket flow: the buyer submits a subject + message, an admin replies
// from the admin panel, and that reply shows up back here in the same thread.
import { React, Link } from "../vendor/runtime.js";
import {
  ArrowLeftIcon,
  ClipboardCheckIcon,
  ClockIcon,
  CircleCheckBigIcon,
  LoaderCircleIcon,
  SquarePenIcon,
  XIcon,
} from "../vendor/runtime.js";
import { useAuth } from "../context/AuthContext.jsx";
import { api } from "../api/client.js";

const STATUS_STYLES = {
  open: { label: "Open", className: "bg-amber-100 text-amber-700", Icon: ClockIcon },
  answered: { label: "Answered", className: "bg-green-100 text-green-700", Icon: CircleCheckBigIcon },
  closed: { label: "Closed", className: "bg-gray-100 text-gray-600", Icon: CircleCheckBigIcon },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.open;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.className}`}>
      <Icon className={"w-3 h-3"} />
      {s.label}
    </span>
  );
};

const SupportWidget = () => {
  const { currentUser } = useAuth();

  const [open, setOpen] = React.useState(false);
  const [view, setView] = React.useState("list"); // 'list' | 'new' | 'thread'
  const [tickets, setTickets] = React.useState([]);
  const [ticketsLoaded, setTicketsLoaded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [subject, setSubject] = React.useState("");
  const [newMessage, setNewMessage] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const [activeTicket, setActiveTicket] = React.useState(null);
  const [replyText, setReplyText] = React.useState("");
  const [sendingReply, setSendingReply] = React.useState(false);

  const threadEndRef = React.useRef(null);

  const loadTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/support-tickets/mine");
      setTickets(res.data || []);
      setTicketsLoaded(true);
    } catch (err) {
      console.error("Error loading tickets:", err);
      setError("Couldn't load your tickets. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openWidget = () => {
    setOpen(true);
    if (currentUser && !ticketsLoaded) loadTickets();
  };

  const closeWidget = () => setOpen(false);

  const goToList = () => {
    setView("list");
    setActiveTicket(null);
    setError(null);
    loadTickets();
  };

  const openTicket = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/support-tickets/mine/${id}`);
      setActiveTicket(res.data);
      setView("thread");
    } catch (err) {
      console.error("Error loading ticket:", err);
      setError("Couldn't load that ticket. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (view === "thread") {
      threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [view, activeTicket]);

  const submitNewTicket = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !newMessage.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post("/support-tickets", { subject: subject.trim(), message: newMessage.trim() });
      setSubject("");
      setNewMessage("");
      await openTicket(res.data.ticketId);
    } catch (err) {
      console.error("Error submitting ticket:", err);
      setError("Couldn't submit your ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || sendingReply || !activeTicket) return;
    setSendingReply(true);
    setError(null);
    try {
      await api.post(`/support-tickets/mine/${activeTicket.id}/reply`, { message: replyText.trim() });
      setReplyText("");
      const res = await api.get(`/support-tickets/mine/${activeTicket.id}`);
      setActiveTicket(res.data);
    } catch (err) {
      console.error("Error sending reply:", err);
      setError("Couldn't send your reply. Please try again.");
    } finally {
      setSendingReply(false);
    }
  };

  // Unified thread: the ticket's own opening message, then every reply, in order.
  const threadMessages = activeTicket
    ? [
        { role: "user", message: activeTicket.message, created_at: activeTicket.created_at },
        ...activeTicket.replies.map((r) => ({
          role: r.sender_role,
          message: r.message,
          created_at: r.created_at,
        })),
      ]
    : [];

  return (
    <div className={"fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40"}>
      {open && (
        <div
          className={
            "mb-3 w-[calc(100vw-2rem)] max-w-sm sm:w-96 h-[28rem] max-h-[70vh] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          }
        >
          <div className={"bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0"}>
            <div className={"flex items-center min-w-0"}>
              {view === "thread" && (
                <button
                  onClick={goToList}
                  className={"mr-2 p-1 rounded-md hover:bg-white/20 transition-colors flex-shrink-0"}
                  aria-label={"Back to tickets"}
                >
                  <ArrowLeftIcon className={"w-4 h-4"} />
                </button>
              )}
              <ClipboardCheckIcon className={"w-5 h-5 mr-2 flex-shrink-0"} />
              <span className={"font-medium truncate"}>
                {view === "thread" && activeTicket ? activeTicket.subject : "Support"}
              </span>
            </div>
            <button
              onClick={closeWidget}
              className={"p-1 rounded-md hover:bg-white/20 transition-colors flex-shrink-0"}
              aria-label={"Close"}
            >
              <XIcon className={"w-5 h-5"} />
            </button>
          </div>

          {!currentUser ? (
            <div className={"flex-1 flex flex-col items-center justify-center text-center p-6"}>
              <ClipboardCheckIcon className={"w-10 h-10 text-gray-300 mb-3"} />
              <p className={"text-sm text-gray-600 mb-4"}>
                {"Please log in to raise a support ticket and track replies."}
              </p>
              <Link
                to={"/login"}
                onClick={closeWidget}
                className={"px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"}
              >
                {"Log In"}
              </Link>
            </div>
          ) : (
            <>
              {view === "list" && (
                <div className={"flex-1 flex flex-col min-h-0"}>
                  <div className={"px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0"}>
                    <span className={"text-sm font-semibold text-gray-700"}>{"My Tickets"}</span>
                    <button
                      onClick={() => {
                        setView("new");
                        setError(null);
                      }}
                      className={"inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-700"}
                    >
                      <SquarePenIcon className={"w-3.5 h-3.5 mr-1"} />
                      {"New Ticket"}
                    </button>
                  </div>
                  <div className={"flex-1 overflow-y-auto"}>
                    {loading ? (
                      <div className={"flex items-center justify-center h-full"}>
                        <LoaderCircleIcon className={"w-6 h-6 text-indigo-400 animate-spin"} />
                      </div>
                    ) : tickets.length === 0 ? (
                      <div className={"flex flex-col items-center justify-center h-full text-center p-6"}>
                        <ClipboardCheckIcon className={"w-10 h-10 text-gray-300 mb-3"} />
                        <p className={"text-sm text-gray-500 mb-4"}>
                          {"No tickets yet. Raise one and our team will reply here."}
                        </p>
                        <button
                          onClick={() => setView("new")}
                          className={"px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"}
                        >
                          {"Raise a Ticket"}
                        </button>
                      </div>
                    ) : (
                      <ul className={"divide-y divide-gray-100"}>
                        {tickets.map((t) => (
                          <li key={t.id}>
                            <button
                              onClick={() => openTicket(t.id)}
                              className={"w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"}
                            >
                              <div className={"flex items-start justify-between gap-2"}>
                                <span className={"text-sm font-medium text-gray-800 truncate"}>{t.subject}</span>
                                <StatusBadge status={t.status} />
                              </div>
                              <p className={"text-xs text-gray-500 mt-1"}>
                                {new Date(t.updated_at).toLocaleString([], {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                                {t.reply_count > 0 && ` · ${t.reply_count} repl${t.reply_count === 1 ? "y" : "ies"}`}
                              </p>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {view === "new" && (
                <form onSubmit={submitNewTicket} className={"flex-1 flex flex-col min-h-0"}>
                  <div className={"flex-1 overflow-y-auto p-4 space-y-3"}>
                    <button
                      type={"button"}
                      onClick={goToList}
                      className={"inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-700 mb-1"}
                    >
                      <ArrowLeftIcon className={"w-3.5 h-3.5 mr-1"} />
                      {"Back"}
                    </button>
                    <div>
                      <label className={"block text-xs font-medium text-gray-600 mb-1"}>{"Subject"}</label>
                      <input
                        type={"text"}
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder={"What's this about?"}
                        className={"w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"}
                        required
                      />
                    </div>
                    <div>
                      <label className={"block text-xs font-medium text-gray-600 mb-1"}>{"Message"}</label>
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={"Describe the issue or question..."}
                        rows={5}
                        className={"w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"}
                        required
                      />
                    </div>
                    {error && <p className={"text-xs text-red-600"}>{error}</p>}
                  </div>
                  <div className={"p-3 border-t border-gray-100 flex-shrink-0"}>
                    <button
                      type={"submit"}
                      disabled={submitting || !subject.trim() || !newMessage.trim()}
                      className={"w-full flex items-center justify-center px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed transition-colors"}
                    >
                      {submitting ? (
                        <LoaderCircleIcon className={"w-4 h-4 animate-spin"} />
                      ) : (
                        "Submit Ticket"
                      )}
                    </button>
                  </div>
                </form>
              )}

              {view === "thread" && activeTicket && (
                <div className={"flex-1 flex flex-col min-h-0"}>
                  <div className={"px-4 py-2 border-b border-gray-100 flex-shrink-0"}>
                    <StatusBadge status={activeTicket.status} />
                  </div>
                  <div className={"flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"}>
                    {threadMessages.map((m, i) => (
                      <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                            m.role === "user"
                              ? "bg-indigo-100 text-indigo-900"
                              : "bg-white border border-gray-200 text-gray-800"
                          }`}
                        >
                          <p className={"whitespace-pre-wrap break-words"}>{m.message}</p>
                          <p className={"text-[10px] text-gray-400 mt-1"}>
                            {m.role === "admin" ? "CodeBruh Support · " : ""}
                            {new Date(m.created_at).toLocaleString([], {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={threadEndRef} />
                  </div>
                  {error && <p className={"text-xs text-red-600 px-4"}>{error}</p>}
                  <form onSubmit={sendReply} className={"p-3 border-t border-gray-100 flex gap-2 flex-shrink-0"}>
                    <input
                      type={"text"}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={"Type a reply..."}
                      disabled={sendingReply}
                      className={"flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"}
                    />
                    <button
                      type={"submit"}
                      disabled={sendingReply || !replyText.trim()}
                      className={"px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed transition-colors flex-shrink-0"}
                    >
                      {sendingReply ? (
                        <LoaderCircleIcon className={"w-4 h-4 animate-spin"} />
                      ) : (
                        <ArrowLeftIcon className={"w-4 h-4 rotate-180"} />
                      )}
                    </button>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!open && (
        <button
          onClick={openWidget}
          className={"w-14 h-14 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center"}
          aria-label={"Open support"}
        >
          <ClipboardCheckIcon className={"w-6 h-6"} />
        </button>
      )}
    </div>
  );
};

export { SupportWidget };
